<template>
	<form class="view-ui-search-bar" v-if="visible" v-on:submit.prevent="Handle_OnSubmit">
		<fieldset class="layout-horizontal gap">
			<label>
				<input
					ref="input"
					type="text"
					v-bind:placeholder="placeholder"
					v-bind:value="localQuery"
					v-on:input="Handle_OnInput"
				/>
				<button class="control round size-small type-close" type="button" v-on:click="Handle_OnClickClear">
					Close
				</button>
			</label>
			<button class="size-small" type="submit">Search</button>
		</fieldset>
	</form>
</template>

<script lang="ts">
	import ViewBase from '@/Core/View.vue';
	import { Component, Prop, Watch } from 'vue-property-decorator';
	import { mounted } from '@/Utility/Decorators';

	/**
	 * Reusable search bar component that syncs its query value with a URL
	 * query parameter. Handles form UI, submission, clearing, and route
	 * synchronization so parent components can watch the URL for changes.
	 *
	 * @author Matt Kenefick <matt@chalkysticks.com>
	 * @package View/Ui
	 * @project ChalkySticks App
	 */
	@Component
	export default class ViewUiSearchBar extends ViewBase {
		/**
		 * Placeholder text for the search input.
		 *
		 * @type string
		 */
		@Prop({ default: 'Search for venue or location' })
		public placeholder!: string;

		/**
		 * The URL query parameter name to sync with.
		 *
		 * @type string
		 */
		@Prop({ default: 'q' })
		public queryParam!: string;

		/**
		 * Whether the search bar is visible.
		 *
		 * @type boolean
		 */
		@Prop({ default: false })
		public visible!: boolean;

		/**
		 * Local query value for the input field.
		 *
		 * @type string
		 */
		protected localQuery: string = '';

		/**
		 * Initialize local query from URL on mount.
		 *
		 * @return void
		 */
		@mounted
		public setup(): void {
			const paramValue = this.getQueryParameter(this.queryParam);

			if (paramValue) {
				this.localQuery = paramValue;
			}
		}

		/**
		 * Watch the route query param for external changes (e.g. back button).
		 *
		 * @param Route to
		 * @param Route from
		 * @return void
		 */
		@Watch('$route.query')
		protected onRouteQueryChange(newQuery: Record<string, string>, oldQuery: Record<string, string>): void {
			const newValue = newQuery[this.queryParam] || '';
			const oldValue = oldQuery[this.queryParam] || '';

			if (newValue !== oldValue) {
				this.localQuery = newValue;

				if (newValue) {
					this.$emit('submit', newValue);
				} else {
					this.$emit('clear');
				}
			}
		}

		// region: Event Handlers
		// ---------------------------------------------------------------------------

		/**
		 * Handle clear button click. Removes query param from URL and emits clear.
		 *
		 * @param PointerEvent e
		 * @return void
		 */
		protected Handle_OnClickClear(e: PointerEvent): void {
			this.localQuery = '';
			this.removeQueryParameter(this.queryParam);
			this.$emit('clear');
		}

		/**
		 * Handle input value changes.
		 *
		 * @param Event e
		 * @return void
		 */
		protected Handle_OnInput(e: Event): void {
			const target = e.target as HTMLInputElement;
			this.localQuery = target.value;
		}

		/**
		 * Handle form submission. Pushes query to URL, which triggers the watcher.
		 *
		 * @param Event e
		 * @return void
		 */
		protected Handle_OnSubmit(e: Event): void {
			const query = this.localQuery.trim();

			if (query.length === 0) {
				return;
			}

			this.setQueryParameter(this.queryParam, query);
		}

		// endregion: Event Handlers
	}
</script>

<style lang="scss">
	.view-ui-search-bar {
		background: var(--chalky-blue-2);
		border-radius: 0.5rem;
		margin: 1rem -0.25rem;

		fieldset {
			padding: 1rem;
		}

		label {
			flex-grow: 1;
			margin: 0;
			width: auto;
		}
	}
</style>
